import React, { useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';

import VisibilitySensor from 'react-visibility-sensor';
import { useIntl, FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';

import Icon from 'components/commons/Icon';
import { ParagraphSmall } from 'components/Typography';
import Spinner from 'components/Spinner';
import ContainerDimensions from 'components/ContainerDimensions';
import Overlay from 'components/Overlay';
import ModalSelectSearchInput from 'components/ModalSelectSearchInput';
import ModalSelectHeader from 'components/ModalSelectHeader';
import ButtonGroup from 'components/ButtonGroup';
import { useOnClickOutside, useFavorites } from 'hooks';
import { getFavoritesFromStorage } from 'utils/storage';

import {
  getCoords,
  getDimensions,
  disableScroll,
  getCurrencyTypeInURLFormat,
  enableScroll,
  getCurrencyUrl,
  getHrefCurrency,
  filterExcludedCurrencies,
} from 'utils';

import { mobileLargeMin } from 'styles';

import {
  DropdownToggle,
  CurrencyLabel,
  DropdownListWrapper,
  DropdownListHeader,
  DesktopSearchInputWrapper,
  DesktopSearchInput,
  ButtonGroupWrapper,
  DropdownList,
  SearchResultsList,
  OptionsTitle,
  Option,
  OptionButton,
  OptionName,
  StarButton,
  StarIcon,
  NoDataLabel,
  SpinnerWrapper,
  MainFlag,
  ListFlag,
  ButtonToggle,
  DropdownName,
  ContainerOptions,
} from './styles';

const interbankCurrency = ['USD', 'EUR'];

const CurrencyDropdown = ({
  currencyInfo,
  isOpen,
  toggleDropdown,
  isFocused,
  isButtonMode,
  searchValue,
  currenciesTypesOptions,
  currenciesType,
  onSelectCurrenciesType,
  onChangeVisibility,
  loading,
  searchLoading,
  items,
  onSelectOption,
  dropdownRef,
  searchItems,
  initSearch,
  pageName,
  onSearchInputChange,
  onFocus,
  onBlur,
  onCancel,
  containerStyle,
  isLocalSearch,
  titleExtractor,
  isNavigator,
  listWrapperStyle,
  optionType,
}) => {
  const intl = useIntl();
  const { locale } = useIntl();
  const containerRef = useRef();
  const mobileDropdownHeaderRef = useRef();
  const mobileSearchRef = useRef();
  const mainOptionTitle = useRef();
  const favoritesOptionTitle = useRef();
  const { date, city } = useParams();
  const history = useHistory();

  const {
    currentFavorites,
    addFavorites,
    handleSwitchCurrencyInFavorites,
  } = useFavorites({ currenciesType });

  const filteredItems = useMemo(() => {
    if (currenciesTypesOptions && items[currenciesType]) {
      return {
        ...items,
        [currenciesType]: filterExcludedCurrencies(items[currenciesType]),
      };
    }
    if (Array.isArray(items)) {
      return filterExcludedCurrencies(items);
    }
    return items;
  }, [items, currenciesType, currenciesTypesOptions]);

  const filteredSearchItems = useMemo(
    () => filterExcludedCurrencies(searchItems || []),
    [searchItems]
  );

  const filteredFavorites = useMemo(
    () => filterExcludedCurrencies(currentFavorites || []),
    [currentFavorites]
  );

  const optionsList = useMemo(() => {
    if (currenciesTypesOptions) {
      return filteredItems[currenciesType] || [];
    }

    if (Array.isArray(filteredItems)) {
      return filteredItems;
    }

    return [];
  }, [currenciesTypesOptions, currenciesType, filteredItems]);

  useEffect(() => {
    if (isOpen) {
      const favorites = getFavoritesFromStorage();
      addFavorites(favorites);
    } else {
      enableScroll();
    }
  }, [isOpen]);

  useOnClickOutside(containerRef, () => {
    if (isOpen) {
      toggleDropdown();
    }
  });

  const onDropdownScroll = () => {
    if (dropdownRef.current.scrollTop > 0) {
      if (mobileSearchRef.current && isFocused && searchValue) {
        mobileSearchRef.current.style.boxShadow =
          '0px 4px 24px rgba(0, 0, 0, 0.14)';
      }

      if (mobileDropdownHeaderRef.current) {
        const optionTitleTop = detectOptionTitleTop();

        const dropdownHeaderHeight = getDimensions(
          mobileDropdownHeaderRef.current
        )?.height;

        mobileDropdownHeaderRef.current.style.boxShadow =
          optionTitleTop !== null && optionTitleTop <= dropdownHeaderHeight
            ? ''
            : '0px 4px 24px rgba(0, 0, 0, 0.14)';
      }
    }

    if (dropdownRef.current.scrollTop === 0) {
      if (mobileSearchRef.current) {
        mobileSearchRef.current.style.boxShadow = '';
      }
      if (mobileDropdownHeaderRef.current) {
        mobileDropdownHeaderRef.current.style.boxShadow = '';
      }
    }
  };

  const detectOptionTitleTop = () => {
    const detectedElement =
      favoritesOptionTitle.current || mainOptionTitle.current;

    if (detectedElement && isOpen) {
      return getCoords(detectedElement).top;
    }

    return null;
  };

  const onClickOption = path => (...data) => {
    if (isOpen) {
      toggleDropdown();
    }

    if (isNavigator) {
      history.push(path);
    }

    onSelectOption(...data);
  };

  const onClickCurrencyOption = event => {
    event.preventDefault();
  };

  const renderOption = currency => {
    const { name, code, slug, type } = currency;
    const isHidden =
      optionType === 'interbank' && !interbankCurrency.includes(code);

    const typeInUrlFormat = getCurrencyTypeInURLFormat(type);
    const value = `${slug}${typeInUrlFormat}`;

    const dataValue = JSON.stringify({
      slug,
      value,
      type,
      typeInUrlFormat,
    });

    const isActive = !!currentFavorites.find(item => item.slug === slug);
    const path = getCurrencyUrl({
      city,
      date,
      currency: slug.toLowerCase(),
      dataSource: pageName,
    });

    const render = () => (
      <Option isHidden={isHidden} key={`${name}-${code}`}>
        <OptionButton data-value={dataValue} onClick={onClickOption(path)}>
          <ListFlag name={value} />
          {code}
          &nbsp;
          <OptionName>{name}</OptionName>
        </OptionButton>

        <StarButton onClick={handleSwitchCurrencyInFavorites(currency)}>
          <StarIcon active={isActive} />
        </StarButton>
      </Option>
    );

    if (isNavigator) {
      return (
        <a href={getHrefCurrency(path, locale)} onClick={onClickCurrencyOption}>
          {render()}
        </a>
      );
    }

    return render();
  };

  const renderButtonMode = () => {
    return (
      <ButtonToggle
        type="button"
        aria-label="dropdown"
        onClick={toggleDropdown}
      >
        <Icon
          name="arrow"
          style={
            isOpen
              ? {
                  transform: 'rotate(180deg)',
                }
              : null
          }
        />
      </ButtonToggle>
    );
  };

  const renderToggle = () => {
    if (titleExtractor) {
      return (
        <DropdownName onClick={toggleDropdown}>
          <FormattedMessage id={titleExtractor()} />
        </DropdownName>
      );
    }

    return isButtonMode ? (
      renderButtonMode()
    ) : (
      <DropdownToggle onClick={toggleDropdown} isOpen={isOpen}>
        <CurrencyLabel>{currencyInfo.code}</CurrencyLabel>
        <MainFlag
          name={`${currencyInfo.slug ||
            currencyInfo.code}${getCurrencyTypeInURLFormat(currencyInfo.type)}`}
        />
        <Icon name="arrow" />
      </DropdownToggle>
    );
  };

  return (
    <ContainerOptions style={{ ...containerStyle }} ref={containerRef}>
      {renderToggle()}
      {isOpen && (
        <DropdownListWrapper
          ref={dropdownRef}
          onScroll={onDropdownScroll}
          style={listWrapperStyle}
        >
          <ContainerDimensions>
            {({ screenWidth }) => {
              const isMobile =
                screenWidth !== 0 && screenWidth < mobileLargeMin;

              if (isOpen && isMobile) {
                disableScroll();
              }

              return (
                <>
                  {isMobile ? (
                    <>
                      <ModalSelectHeader
                        innerRef={mobileDropdownHeaderRef}
                        title="Список валют"
                        closeTitle={intl.formatMessage({ id: 'close' })}
                        onClose={toggleDropdown}
                        hidden={isFocused}
                      />

                      <ModalSelectSearchInput
                        ref={mobileSearchRef}
                        isFocused={isFocused}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onChange={onSearchInputChange}
                        value={searchValue}
                        onCancel={onCancel}
                        label={intl.formatMessage({
                          id: `currencyDropdown.searchInput.label`,
                        })}
                        placeholder={intl.formatMessage({
                          id: 'currencyDropdown.searchInput.placeholder',
                        })}
                        cancelLabel={intl.formatMessage({
                          id: 'currencyDropdown.searchInput.cancelButtonLabel',
                        })}
                      />
                    </>
                  ) : (
                    <DropdownListHeader>
                      <DesktopSearchInputWrapper isFocused={isFocused}>
                        <Icon name="search" />

                        <DesktopSearchInput
                          type="text"
                          value={searchValue}
                          onFocus={onFocus}
                          onBlur={onBlur}
                          onChange={onSearchInputChange}
                          autoComplete="off"
                          placeholder={intl.formatMessage({
                            id: `currencyDropdown.searchInput.placeholder`,
                          })}
                        />
                      </DesktopSearchInputWrapper>
                    </DropdownListHeader>
                  )}

                  {currenciesTypesOptions && (
                    <ButtonGroupWrapper isFocused={isFocused && isMobile}>
                      <ButtonGroup
                        options={currenciesTypesOptions}
                        value={currenciesType}
                        onSelect={onSelectCurrenciesType}
                      />
                    </ButtonGroupWrapper>
                  )}

                  <Overlay
                    show={!searchValue && isFocused && isMobile}
                    offset={72}
                  />
                  {!!searchValue && !isLocalSearch && (
                    <VisibilitySensor
                      partialVisibility="bottom"
                      containment={dropdownRef.current}
                      onChange={onChangeVisibility}
                    >
                      <SearchResultsList
                        onClick={isMobile && !searchValue ? onBlur : null}
                        isFocused={isFocused}
                        isMobile={isMobile}
                      >
                        {filteredSearchItems.map(renderOption)}
                        {searchItems.length === 0 &&
                          !loading &&
                          !searchLoading &&
                          initSearch && (
                            <NoDataLabel>
                              <ParagraphSmall>
                                {intl.formatMessage({
                                  id: 'noData.noFound',
                                })}
                              </ParagraphSmall>
                              <ParagraphSmall>
                                {intl.formatMessage({
                                  id: 'noData.tryAnother',
                                })}
                              </ParagraphSmall>
                            </NoDataLabel>
                          )}
                      </SearchResultsList>
                    </VisibilitySensor>
                  )}
                  {(!searchValue || isLocalSearch) && (
                    <VisibilitySensor
                      partialVisibility="bottom"
                      containment={dropdownRef.current}
                      onChange={onChangeVisibility}
                    >
                      <DropdownList isFocused={isFocused} isMobile={isMobile}>
                        {filteredFavorites.length > 0 && (
                          <>
                            <OptionsTitle ref={favoritesOptionTitle}>
                              {intl.formatMessage({ id: 'favorites' })}
                            </OptionsTitle>

                            {filteredFavorites.map(renderOption)}
                          </>
                        )}

                        <OptionsTitle ref={mainOptionTitle}>
                          {intl.formatMessage({
                            id: `currenciesType.${currenciesType}.long`,
                          })}
                        </OptionsTitle>

                        {optionsList.map(renderOption)}
                      </DropdownList>
                    </VisibilitySensor>
                  )}
                  {(loading || searchLoading) && (
                    <SpinnerWrapper>
                      <Spinner />
                    </SpinnerWrapper>
                  )}
                </>
              );
            }}
          </ContainerDimensions>
        </DropdownListWrapper>
      )}
    </ContainerOptions>
  );
};

export default CurrencyDropdown;

CurrencyDropdown.propTypes = {
  toggleDropdown: PropTypes.func.isRequired,
  isButtonMode: PropTypes.bool,
  currenciesType: PropTypes.string,
  searchItems: PropTypes.arrayOf(PropTypes.shape({})),
  containerStyle: PropTypes.shape({}),
  listWrapperStyle: PropTypes.shape({}),
  onCancel: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  isLocalSearch: PropTypes.bool,
  loading: PropTypes.bool,
  searchLoading: PropTypes.bool,
  initSearch: PropTypes.bool,
  isNavigator: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  isFocused: PropTypes.bool.isRequired,
  items: PropTypes.shape(PropTypes.arrayOf).isRequired,
  onSelectOption: PropTypes.func.isRequired,
  onSearchInputChange: PropTypes.func.isRequired,
  onChangeVisibility: PropTypes.func,
  onSelectCurrenciesType: PropTypes.func,
  searchValue: PropTypes.string,
  currencyInfo: PropTypes.shape({
    code: PropTypes.string,
    slug: PropTypes.string,
    type: PropTypes.string,
  }),
  currenciesTypesOptions: PropTypes.arrayOf(PropTypes.shape({})),
  titleExtractor: PropTypes.func,
  dropdownRef: PropTypes.shape({
    current: PropTypes.shape({
      scrollTop: PropTypes.number,
    }),
  }).isRequired,
  optionType: PropTypes.string.isRequired,
  pageName: PropTypes.string,
};

CurrencyDropdown.defaultProps = {
  searchItems: [],
  isButtonMode: false,
  currenciesType: 'money',
  containerStyle: {},
  listWrapperStyle: {},
  isLocalSearch: false,
  isNavigator: false,
  initSearch: false,
  loading: false,
  searchLoading: false,
  searchValue: '',
  pageName: '',
  currencyInfo: undefined,
  currenciesTypesOptions: undefined,
  titleExtractor: undefined,
  onChangeVisibility: undefined,
  onSelectCurrenciesType: undefined,
};
